import {
  Bookmark, BookmarkCheck, ArrowUpRight, Euro, CalendarClock, CheckCircle2, HelpCircle,
  Globe, GitCompare, ShieldCheck, ThumbsUp, ThumbsDown, Clock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { scoreTone, statoTone, etichettaFor, fmtCheckDate } from "@/lib/constants";

const FB_ICON = { ThumbsUp, ThumbsDown, Clock };

export function GrantCard({ grant, saved, onToggleFavorite, onOpen, comparing, onToggleCompare, compareDisabled, feedback, onFeedback }) {
  const tone = scoreTone(grant.compatibilita || 0);
  const eti = etichettaFor(grant);
  const checkDate = fmtCheckDate(grant.ultimo_controllo);
  const confermati = grant.requisiti_confermati || grant.criteri_compatibili || [];
  const daVerificare = grant.requisiti_da_verificare || [];

  const fbBtns = [
    { key: "pertinente", label: "Pertinente", Icon: ThumbsUp, on: "bg-emerald-600 text-white border-emerald-600" },
    { key: "scaduto", label: "Scaduto", Icon: Clock, on: "bg-amber-500 text-white border-amber-500" },
    { key: "falso_positivo", label: "Falso positivo", Icon: ThumbsDown, on: "bg-red-600 text-white border-red-600" },
  ];

  return (
    <div
      data-testid="grant-card-item"
      className={`animate-in-up group relative flex flex-col rounded-2xl border bg-white p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-lg ${
        comparing ? "border-indigo-500 ring-2 ring-indigo-200" : "border-slate-200 hover:border-indigo-300"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`rounded-md px-2 py-1 text-[11px] font-bold ${eti.cls}`} data-testid="grant-etichetta">
            {eti.label}
          </span>
          <span className="rounded-md bg-slate-100 px-2 py-1 text-[11px] font-semibold text-slate-600">
            {grant.ente}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {onToggleCompare && (
            <button
              data-testid="grant-compare-toggle-button"
              onClick={() => onToggleCompare(grant)}
              disabled={!comparing && compareDisabled}
              title="Aggiungi al confronto"
              className={`rounded-lg p-2 transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                comparing ? "bg-indigo-600 text-white" : "text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              }`}
            >
              <GitCompare className="h-5 w-5" />
            </button>
          )}
          <button
            data-testid="grant-favorite-toggle-button"
            onClick={() => onToggleFavorite(grant)}
            className={`rounded-lg p-2 transition-colors ${
              saved ? "bg-indigo-50 text-indigo-600" : "text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            }`}
            aria-label="Salva nei preferiti"
          >
            {saved ? <BookmarkCheck className="h-5 w-5" /> : <Bookmark className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <h3 className="mt-3 font-display text-lg font-bold leading-tight text-slate-900">{grant.titolo}</h3>
      <p className="mt-1 text-sm text-slate-500">{grant.sottotitolo}</p>

      {/* Compatibilità come indicazione secondaria + su cosa si basa */}
      <div className="mt-4 flex items-center gap-2">
        <div className={`flex items-center gap-2 rounded-lg border px-2.5 py-1 ${tone.cls}`}>
          <span className="font-mono text-sm font-bold">{grant.compatibilita}%</span>
          <span className="text-[11px] font-semibold">compatibilità stimata</span>
        </div>
      </div>
      {grant.motivazione_match && (
        <p className="mt-1.5 text-xs italic text-slate-500">Base: {grant.motivazione_match}</p>
      )}

      <div className="mt-4 grid grid-cols-1 gap-2 text-sm">
        <div className="flex items-center gap-2 text-slate-700">
          <Euro className="h-4 w-4 shrink-0 text-emerald-600" />
          <span className="font-semibold">{grant.importo_max || "Da verificare"}</span>
        </div>
        <div className="flex items-center gap-2 text-slate-600">
          <CalendarClock className="h-4 w-4 shrink-0 text-amber-500" />
          <span>Scadenza: {grant.scadenza || "Da verificare"}</span>
        </div>
      </div>

      {/* Requisiti confermati vs da verificare */}
      {(confermati.length > 0 || daVerificare.length > 0) && (
        <div className="mt-4 space-y-2 rounded-xl border border-slate-100 bg-slate-50 p-3">
          {confermati.length > 0 && (
            <div>
              <span className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-emerald-700">
                <ShieldCheck className="h-3.5 w-3.5" /> Requisiti confermati
              </span>
              <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="grant-req-confirmed">
                {confermati.slice(0, 4).map((c, i) => (
                  <span key={i} className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[11px] font-medium text-emerald-700 ring-1 ring-emerald-200">
                    <CheckCircle2 className="h-3 w-3" />{c}
                  </span>
                ))}
              </div>
            </div>
          )}
          {daVerificare.length > 0 && (
            <div>
              <span className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-amber-700">
                <HelpCircle className="h-3.5 w-3.5" /> Da verificare
              </span>
              <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="grant-req-toverify">
                {daVerificare.slice(0, 4).map((c, i) => (
                  <span key={i} className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[11px] font-medium text-amber-700 ring-1 ring-amber-200">
                    <HelpCircle className="h-3 w-3" />{c}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Fonte + ultimo controllo */}
      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-400">
        {grant.link_ufficiale && (
          <a href={grant.link_ufficiale} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1 font-medium text-indigo-600 hover:underline" data-testid="grant-source-inline-link">
            <Globe className="h-3.5 w-3.5" /> Fonte ufficiale
          </a>
        )}
        {checkDate && (
          <span className="inline-flex items-center gap-1" data-testid="grant-last-check">
            <Clock className="h-3.5 w-3.5" /> Controllato il {checkDate}
          </span>
        )}
      </div>

      {/* Feedback qualità */}
      {onFeedback && (
        <div className="mt-3 border-t border-slate-100 pt-3">
          <span className="text-[11px] font-medium text-slate-400">Questo risultato è utile?</span>
          <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="grant-feedback">
            {fbBtns.map((b) => {
              const active = feedback === b.key;
              return (
                <button
                  key={b.key}
                  onClick={() => onFeedback(grant, active ? null : b.key)}
                  data-testid={`feedback-${b.key}`}
                  className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors ${
                    active ? b.on : "border-slate-200 text-slate-500 hover:bg-slate-100"
                  }`}
                >
                  <b.Icon className="h-3 w-3" />{b.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-auto flex gap-2 pt-5">
        <Button variant="secondary" className="flex-1" data-testid="grant-details-modal-button" onClick={() => onOpen(grant)}>
          Dettagli & Analisi
        </Button>
        {grant.link_ufficiale && (
          <Button asChild variant="outline" className="gap-1.5">
            <a href={grant.link_ufficiale} target="_blank" rel="noopener noreferrer" data-testid="grant-card-official-link">
              Portale
              <ArrowUpRight className="h-4 w-4" />
            </a>
          </Button>
        )}
      </div>
    </div>
  );
}
